/**
 * FeedManager Page
 * Glavna stranica za upravljanje XML/SOAP feedovima
 *
 * Refaktorirano: Razdvojeno u manje komponente i hook-ove za bolju održivost
 */

import { useEffect, useCallback, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
} from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { parseXmlStructure, parseJsonStructure, isJsonContent, FieldTransformOptions } from '../../lib/xmlParser';

// Hooks
import {
  useFeedProfile,
  useFeedParser,
  useImport,
  useSupplierPriority,
} from './hooks';

// Components
import {
  ProfileList,
  SupplierPriority,
  UpdateFieldsPanel,
  FeedUrlTab,
  MappingTab,
  PreviewTab,
  TabPanel,
  CronManager,
  DatabaseStats,
  RowFiltersPanel,
} from './components';

// Types
import { FeedProfile, CustomField, RowFilter } from './types';

export default function FeedManager() {
  // ============================================================================
  // HOOKS
  // ============================================================================

  const profile = useFeedProfile();
  const parser = useFeedParser();
  const importHook = useImport();
  const supplierPriority = useSupplierPriority();

  // Local tab state
  const [tabValue, setTabValue] = useState(0);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    profile.loadProfiles();
    supplierPriority.loadPriorities();
  }, []);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  /**
   * Handle profile selection - load profile and fetch feed
   */
  const handleSelectProfile = useCallback(async (feedProfile: FeedProfile) => {
    profile.selectProfile(feedProfile);

    // Automatically fetch feed if URL exists
    if (feedProfile.feed_url) {
      parser.setExpanded(new Set());

      try {
        let fetchUrlParams = `url=${encodeURIComponent(feedProfile.feed_url)}`;
        if (
          feedProfile.auth_type !== 'none' &&
          feedProfile.auth_config?.username &&
          feedProfile.auth_config?.password
        ) {
          fetchUrlParams += `&username=${encodeURIComponent(feedProfile.auth_config.username)}&password=${encodeURIComponent(feedProfile.auth_config.password)}`;
          fetchUrlParams += `&auth_type=${encodeURIComponent(feedProfile.auth_type)}`;
        }
        if (feedProfile.extra_headers && Object.keys(feedProfile.extra_headers).length > 0) {
          fetchUrlParams += `&extra_headers=${encodeURIComponent(JSON.stringify(feedProfile.extra_headers))}`;
        }

        const response = await fetch(`/api/feed/fetch?${fetchUrlParams}`);
        const result = await response.json();

        if (result.success) {
          parser.setXmlContent(result.content);
          const parsed = isJsonContent(result.content)
            ? parseJsonStructure(result.content)
            : parseXmlStructure(result.content);
          parser.setParsedFeed(parsed);
          parser.setExpanded(new Set([parsed.rootElement]));
          toast.success(`Feed učitan: ${parsed.totalRecords} zapisa`);
          setTabValue(1); // Go to mapping tab
        } else {
          toast.error('Greška pri učitavanju feeda');
          setTabValue(0);
        }
      } catch (error: any) {
        console.error('Error loading feed:', error);
        toast.error('Greška: ' + error.message);
        setTabValue(0);
      }
    } else {
      setTabValue(0);
    }
  }, [profile, parser]);

  /**
   * Handle fetch feed button click
   */
  const handleFetchFeed = useCallback(async () => {
    const newMappings = await parser.fetchFeed(
      profile.feedUrl,
      profile.authType,
      profile.authUsername,
      profile.authPassword,
      profile.mappings,
      profile.feedType,
      profile.soapConfig,
      profile.extraHeaders
    );

    if (newMappings) {
      // Only update mappings if we got auto-detected ones
      if (Object.keys(profile.mappings).length === 0) {
        profile.setMappings(newMappings);
      }
      setTabValue(1); // Go to mapping tab
    }
  }, [parser, profile]);

  /**
   * Handle local file upload (XML/XLS/XLSX)
   */
  const handleLoadFile = useCallback(async (file: File) => {
    const newMappings = await parser.loadFile(file, profile.mappings);
    if (newMappings && Object.keys(profile.mappings).length === 0) {
      profile.setMappings(newMappings);
    }
    if (parser.parsedFeed || newMappings) {
      setTabValue(1);
    }
  }, [parser, profile]);

  /**
   * Handle new profile button
   */
  const handleNewProfile = useCallback(() => {
    profile.clearProfile();
    parser.clearFeed();
    setTabValue(0);
  }, [profile, parser]);

  /**
   * Handle mapping change
   */
  const handleMappingChange = useCallback((dbField: string, xmlPath: string) => {
    profile.setMappings(prev => ({
      ...prev,
      [dbField]: xmlPath,
    }));
  }, [profile]);

  /**
   * Handle field option change
   */
  const handleFieldOptionChange = useCallback((
    dbField: string,
    option: Partial<FieldTransformOptions>
  ) => {
    profile.setFieldOptions(prev => ({
      ...prev,
      [dbField]: {
        mode: 'single',
        ...prev[dbField],
        ...option,
      },
    }));
  }, [profile]);

  /**
   * Handle add custom field
   */
  const handleAddCustomField = useCallback((field: CustomField) => {
    profile.setCustomFields(prev => [...prev, field]);
  }, [profile]);

  /**
   * Handle remove custom field
   */
  const handleRemoveCustomField = useCallback((id: string) => {
    const field = profile.customFields.find(f => f.id === id);
    if (field) {
      // Remove from mappings too
      profile.setMappings(prev => {
        const newMappings = { ...prev };
        delete newMappings[field.key];
        return newMappings;
      });
    }
    profile.setCustomFields(prev => prev.filter(f => f.id !== id));
  }, [profile]);

  /**
   * Handle update field change
   */
  const handleUpdateFieldChange = useCallback((key: string, checked: boolean) => {
    profile.setUpdateFields(prev => ({
      ...prev,
      [key]: checked,
    }));
  }, [profile]);

  /**
   * Handle import
   */
  const handleImport = useCallback(async () => {
    await importHook.runImport({
      xmlContent: parser.xmlContent,
      mappings: profile.mappings,
      fieldOptions: profile.fieldOptions,
      customFields: profile.customFields,
      feedName: profile.feedName,
      updateFields: profile.updateFields,
      profileId: profile.selectedProfile?.id,
      importMode: profile.importMode,
      // For Excel feeds: pass all parsed records (not just sample 50)
      sampleRecords: parser.parsedFeed?.sampleRecords,
      rowFilters: profile.rowFilters,
    });
  }, [importHook, parser, profile]);

  // ============================================================================
  // RENDER
  // ============================================================================

  const saveDisabled = !profile.feedName || !profile.mappings['sku'];

  return (
    <Box>
      {/* Header */}
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SettingsIcon /> Feed Manager
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Konfiguriši XML/SOAP feedove - jednom podesi, zauvijek radi
      </Typography>

      <Grid container spacing={2}>
        {/* Left side - Profiles */}
        <Grid item xs={12} lg={2}>
          <ProfileList
            profiles={profile.profiles}
            selectedProfile={profile.selectedProfile}
            onSelectProfile={handleSelectProfile}
            onNewProfile={handleNewProfile}
          >
            {/* Supplier Priority Panel */}
            <SupplierPriority
              priorities={supplierPriority.priorities}
              onMovePriority={supplierPriority.movePriority}
            />

            {/* Update Fields Panel */}
            <UpdateFieldsPanel
              updateFields={profile.updateFields}
              onUpdateFieldChange={handleUpdateFieldChange}
              selectedProfile={profile.selectedProfile}
              importMode={profile.importMode}
              onImportModeChange={profile.setImportMode}
              skipDuplicateCheck={profile.skipDuplicateCheck}
              onSkipDuplicateCheckChange={profile.setSkipDuplicateCheck}
            />

            {/* Database Statistics */}
            <DatabaseStats />
          </ProfileList>
        </Grid>

        {/* Right side - Configuration */}
        <Grid item xs={12} lg={10}>
          <Card>
            <CardContent>
              <Tabs
                value={tabValue}
                onChange={(_, v) => setTabValue(v)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
              >
                <Tab label="1. Feed URL" />
                <Tab label="2. Mapiranje polja" disabled={!parser.parsedFeed} />
                <Tab label="3. Preview & Import" disabled={!parser.parsedFeed} />
                <Tab label="4. Cron Scheduler" />
              </Tabs>

              {/* Tab 1: Feed URL */}
              <TabPanel value={tabValue} index={0}>
                <FeedUrlTab
                  feedName={profile.feedName}
                  onFeedNameChange={profile.setFeedName}
                  feedUrl={profile.feedUrl}
                  onFeedUrlChange={profile.setFeedUrl}
                  feedType={profile.feedType}
                  onFeedTypeChange={profile.setFeedType}
                  authType={profile.authType}
                  onAuthTypeChange={profile.setAuthType}
                  authUsername={profile.authUsername}
                  onAuthUsernameChange={profile.setAuthUsername}
                  authPassword={profile.authPassword}
                  onAuthPasswordChange={profile.setAuthPassword}
                  soapConfig={profile.soapConfig}
                  onSoapConfigChange={profile.setSoapConfig}
                  extraHeaders={profile.extraHeaders}
                  onExtraHeadersChange={profile.setExtraHeaders}
                  deliveryDays={profile.deliveryDays}
                  onDeliveryDaysChange={profile.setDeliveryDays}
                  loading={parser.loading}
                  onFetchFeed={handleFetchFeed}
                  onLoadFile={handleLoadFile}
                  parsedFeed={parser.parsedFeed}
                  loadedFileName={parser.loadedFileName}
                />

                {/* Row Filters - prikaži kad je feed učitan */}
                {parser.parsedFeed && (
                  <RowFiltersPanel
                    rowFilters={profile.rowFilters}
                    onRowFiltersChange={profile.setRowFilters}
                    availableFields={parser.parsedFeed.fields.map(f => f.path)}
                    sampleRecords={parser.parsedFeed.sampleRecords}
                  />
                )}
              </TabPanel>

              {/* Tab 2: Field Mapping */}
              <TabPanel value={tabValue} index={1}>
                {parser.parsedFeed && (
                  <MappingTab
                    parsedFeed={parser.parsedFeed}
                    feedName={profile.feedName}
                    mappings={profile.mappings}
                    fieldOptions={profile.fieldOptions}
                    customFields={profile.customFields}
                    sampleIndex={parser.sampleIndex}
                    expanded={parser.expanded}
                    onMappingChange={handleMappingChange}
                    onFieldOptionChange={handleFieldOptionChange}
                    onAddCustomField={handleAddCustomField}
                    onRemoveCustomField={handleRemoveCustomField}
                    onSampleIndexChange={parser.setSampleIndex}
                    onToggleExpand={parser.toggleExpand}
                    onSave={profile.saveProfile}
                    saveDisabled={saveDisabled}
                  />
                )}
              </TabPanel>

              {/* Tab 3: Preview & Import */}
              <TabPanel value={tabValue} index={2}>
                {parser.parsedFeed && (
                  <PreviewTab
                    parsedFeed={parser.parsedFeed}
                    feedName={profile.feedName}
                    mappings={profile.mappings}
                    fieldOptions={profile.fieldOptions}
                    customFields={profile.customFields}
                    rowFilters={profile.rowFilters}
                    importing={importHook.importing}
                    importProgress={importHook.progress}
                    importResults={importHook.results}
                    onSave={profile.saveProfile}
                    onImport={handleImport}
                    saveDisabled={saveDisabled}
                  />
                )}
              </TabPanel>

              {/* Tab 4: Cron Scheduler */}
              <TabPanel value={tabValue} index={3}>
                <CronManager />
              </TabPanel>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
